import type { StarterChip } from "../lineHelpers";

// ---- LA LÍNEA DE QUIEN LE VENDE AL ESTADO ----
// Espejo de la vista previa en `backend/src/services/onboarding.service.ts`
// (`getContractorPreview`): licitaciones convocadas de su objeto. Si cambian de
// un lado y no del otro, el número del onboarding deja de cuadrar con el dashboard.
export const CONTRACTOR_STAGES = ["CONVOCADO"];

// ---- CHIPS DE ARRANQUE ----
// Curados a mano, sin IA: cada uno trae cómo se titulan esas obras en el SEACE.
const WORKS_STARTERS: StarterChip[] = [
  {
    label: "Pistas y veredas",
    products: [
      { label: "pista", term: "pista" },
      { label: "vereda", term: "vereda" },
      { label: "transitabilidad", term: "transitabilidad" },
    ],
  },
  {
    label: "Saneamiento",
    products: [
      { label: "saneamiento", term: "saneamiento" },
      { label: "agua potable", term: "agua potable" },
      { label: "alcantarillado", term: "alcantarillado" },
    ],
  },
  {
    label: "Edificaciones",
    products: [
      { label: "institución educativa", term: "institución educativa" },
      { label: "centro de salud", term: "centro de salud" },
      { label: "local comunal", term: "local comunal" },
    ],
  },
  {
    label: "Carreteras",
    products: [
      { label: "carretera", term: "carretera" },
      { label: "camino vecinal", term: "camino vecinal" },
    ],
  },
  { label: "Puentes", products: [{ label: "puente", term: "puente" }] },
  {
    label: "Riego",
    products: [
      { label: "riego", term: "riego" },
      { label: "canal", term: "canal" },
    ],
  },
  {
    label: "Electrificación",
    products: [
      { label: "electrificación", term: "electrificación" },
      { label: "energía eléctrica", term: "energía eléctrica" },
    ],
  },
];

// Los de bienes y servicios salen de la evaluación del sugeridor (ago-2026): términos
// que sí aparecen en los ítems de las licitaciones, con decenas o cientos de casos.
const GOODS_STARTERS: StarterChip[] = [
  {
    label: "Uniformes",
    products: [
      { label: "uniforme", term: "uniforme" },
      { label: "vestuario", term: "vestuario" },
    ],
  },
  {
    label: "Computadoras",
    products: [
      { label: "computadora", term: "computador" },
      { label: "laptop", term: "laptop" },
    ],
  },
  {
    label: "Cemento y agregados",
    products: [
      { label: "cemento", term: "cemento" },
      { label: "agregado", term: "agregado" },
    ],
  },
  { label: "Ladrillos", products: [{ label: "ladrillo", term: "ladrillo" }] },
  {
    label: "Geomembranas",
    products: [
      { label: "geomembrana", term: "geomembran" },
      { label: "geotextil", term: "geotextil" },
    ],
  },
];

const SERVICES_STARTERS: StarterChip[] = [
  {
    label: "Limpieza",
    products: [
      { label: "servicio de limpieza", term: "servicio de limpieza" },
      { label: "limpieza y mantenimiento", term: "limpieza y mantenimiento" },
    ],
  },
  {
    label: "Alquiler de maquinaria",
    products: [
      { label: "volquete", term: "volquete" },
      { label: "excavadora", term: "excavadora" },
      { label: "cargador frontal", term: "cargador frontal" },
    ],
  },
  {
    label: "Transporte",
    products: [
      { label: "transporte de personal", term: "transporte de personal" },
      { label: "alquiler de camioneta", term: "alquiler de camioneta" },
    ],
  },
  { label: "Señalización", products: [{ label: "señalización", term: "señaliz" }] },
];

// ---- QUÉ LE VENDE AL ESTADO ----
// Solo las obras tienen rangos de tamaño y cruce con documentos: son las únicas
// cuyas bases leemos hoy.
export type SaleSubject = "works" | "goods" | "services" | "consulting";

export const SALE_SUBJECTS: Record<
  SaleSubject,
  {
    label: string;
    detail: string;
    /** El objeto que guarda la línea. */
    lineSubjects: string[];
    /** La etiqueta de cada fila: "Obra", "Bien", "Servicio", "Consultoría". */
    tag: string;
    one: string;
    many: string;
    question: string;
    placeholder: string;
    starters: StarterChip[];
    zoneLead: string;
  }
> = {
  works: {
    label: "Obras",
    detail: "Ejecuto obras públicas: pistas, saneamiento, edificaciones.",
    lineSubjects: ["works"],
    tag: "Obra",
    one: "obra",
    many: "obras",
    question: "¿Qué obras haces?",
    placeholder: "Ej. mejoramiento de caminos vecinales",
    starters: WORKS_STARTERS,
    zoneLead: "Los departamentos donde puedes ejecutar obras.",
  },
  goods: {
    label: "Bienes",
    detail: "Vendo productos: uniformes, computadoras, materiales.",
    lineSubjects: ["goods"],
    tag: "Bien",
    one: "licitación",
    many: "licitaciones",
    question: "¿Qué bienes vendes?",
    placeholder: "Ej. uniformes para personal de salud",
    starters: GOODS_STARTERS,
    zoneLead: "Los departamentos donde puedes entregar.",
  },
  services: {
    label: "Servicios",
    detail: "Presto servicios: limpieza, transporte, alquiler de maquinaria.",
    lineSubjects: ["services"],
    tag: "Servicio",
    one: "licitación",
    many: "licitaciones",
    question: "¿Qué servicios das?",
    placeholder: "Ej. limpieza de oficinas",
    starters: SERVICES_STARTERS,
    zoneLead: "Los departamentos donde puedes prestar tus servicios.",
  },
  consulting: {
    label: "Consultoría de obra",
    detail: "Supervisión, expedientes técnicos, estudios.",
    // El OECE la publica como servicio y el scraper la reclasifica después: la línea la ve desde ahí.
    lineSubjects: ["consulting"],
    tag: "Consultoría",
    one: "licitación",
    many: "licitaciones",
    question: "¿Qué consultorías haces?",
    placeholder: "Ej. supervisión de obras de saneamiento",
    // Sin chips: los términos de consultoría medidos en ago-2026 traían muy pocos casos.
    starters: [],
    zoneLead: "Los departamentos donde puedes trabajar.",
  },
};

export type SaleCopy = (typeof SALE_SUBJECTS)[SaleSubject];

const DAY_MS = 24 * 60 * 60 * 1000;

/** "Cierra hoy", "Cierra en N días" o "Registro cerrado", con el color de la urgencia. */
export function closingLabel(registrationEndsAt: string | null): {
  label: string;
  className: string;
} | null {
  if (!registrationEndsAt) return null;
  const days = Math.ceil((new Date(registrationEndsAt).getTime() - Date.now()) / DAY_MS);
  if (days < 0) return { label: "Registro cerrado", className: "text-slate-400" };
  if (days === 0) return { label: "Cierra hoy", className: "text-red-600" };
  const label = days === 1 ? "Cierra mañana" : `Cierra en ${days} días`;
  if (days <= 3) return { label, className: "text-red-600" };
  if (days <= 7) return { label, className: "text-orange-600" };
  return { label, className: "text-slate-500" };
}
